import React, { useState } from 'react'
import { useClient, useMutation, useQuery } from 'jsonapi-react'
import { Trash2 } from 'react-feather'
import { Helmet } from 'react-helmet-async'
import { Layout } from 'app/theme/layout'
import Input from 'app/theme/forms/Input'
import Select from 'app/theme/forms/Select'
import useSessionData from 'app/utils/useSessionData'
import useEventTracker from 'lib/useEventTracker'
import locations from './locations'
import PhotoUpload from '../photos/PhotoUpload'
import styles from './AddListingPage.module.css'

export default function AddListingPage() {
  const [sessionData] = useSessionData()

  const [validations, setValidations] = useState({})
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [success, setSuccess] = useState(false)
  const [finished, setFinished] = useState(false)

  const client = useClient()
  const tracker = useEventTracker()

  const [addMarketListing, { data, setData, error, errors }] = useMutation('market_listings', {
    initialData: {
      vehicle_type: 'truck',
      fuel_type: 'gas',
      title: '',
      price: '',
      vehicle_city: '',
      vehicle_state: '',
      vehicle_zip: '',
      year: '',
      make: '',
      model: '',
      mileage: '',
      length: '',
      height: '',
      permits: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      vehicle_features: [],
      photos: [],
      meta: sessionData.meta
    }
  })

  const featureCategories = useQuery(['vehicle_feature_categories', {
    include: ['vehicle_features'],
  }])

  const updateVehicleFeatures = (feature) => {

    if (!feature) { return }

    setData({
      ...data,
      vehicle_features:
        data.vehicle_features.find(f => f.id === feature.id)
          ? data.vehicle_features.filter(f => f.id !== feature.id )
          : data.vehicle_features.concat({id: feature.id})
    })
  }

  const validate = () => {
    const v = {}
    const emailRegex = /^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

    if (!data.title) {
      v.title = 'Please enter a title'
    }
    if (!data.price) {
      v.price = 'Please enter a price'
    }
    if (!data.vehicle_state) {
      v.location = 'Please choose a state/region'
    }
    if (!data.year) {
      v.year = 'Please enter a year'
    }
    if (!data.make) {
      v.make = 'Please enter a make'
    }
    if (!data.model) {
      v.model = 'Please enter a model'
    }
    if (!data.mileage) {
      v.mileage = 'Please enter the mileage'
    } else if (isNaN(Number(data.mileage))) {
      v.mileage = 'Mileage needs to be an integer'
    }
    if (!data.first_name) {
      v.first_name = 'Please provide a first name'
    }
    if (!data.last_name) {
      v.last_name = 'Please provide a last name'
    }
    if (!data.email) {
      v.email = 'Please enter an email'
    } else if (!emailRegex.test(data.email)) {
      v.email = 'Please enter a valid email'
    }
    if (!data.phone) {
      v.phone = 'Please enter a phone number'
    }
    if (!data.address) {
      v.address = 'Please enter an address'
    }
    if (!data.vehicle_city) {
      v.vehicle_city = 'Please enter a city'
    }
    if (!data.vehicle_state) {
      v.vehicle_state = 'Please choose a state'
    }
    if (!data.city) {
      v.city = 'Please enter a city'
    }
    if (!data.state) {
      v.state = 'Please choose a state'
    }
    if (!data.zip) {
      v.zip = 'Please enter a zip code'
    }

    setValidations(v)
    return !Object.keys(v).length
  }

  const formatPhone = str => {
    const clean = ('' + str).replace(/\D/g, '');
    const match = clean.match(/^(\d{3})(\d{1,3})?(\d{1,4})?(.*)/)

    if (!match) {
      return clean ? `(${clean}` : ''
    } else if (match[3]) {
      return `(${match[1]}) ${match[2]}-${match[3]}${match[4]}`
    } else if (match[2]) {
      return `(${match[1]}) ${match[2]}`
    } else {
      return `(${match[1]}) `
    }
  }

  const submitForm = e => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    tracker.event('Submit', {
      event_category: 'Sell Truck Form',
    })

    return addMarketListing({
      ...data,
      mileage: Math.round(Number(data.mileage)),
    }).then(response => {
      if (response.data) {
        setSuccess(true)
        tracker.event('Success', {
          event_category: 'Sell Truck Form',
        })
      } else {
        tracker.event('Error', {
          event_category: 'Sell Truck Form',
        })
      }
    })
  }

  const removePhoto = (photo) => {
    client.delete(['photos', photo.id, {token: data.token}])
    setData({
      ...data,
      photos: data.photos.filter(ph => ph.id !== photo.id)
    })
  }

  const afterUpload = (result) => {
    setData({
      ...data,
      photos: data.photos ? data.photos.concat(result) : [result]
    })
  }

  const FormWrapper = success ? 'div' : 'form'

  return (
    <Layout className="container pt-10 m-auto max-w-3xl">
      <Helmet>
        <title>Add Listing - Mobile Food Alliance</title>
        <meta name="description" content="Sell your food truck, cart, trailer, or other equipment on the Mobile Food Alliance marketplace." />
        <meta name="og:title" content="Seller form - Mobile Food Alliance" />
      </Helmet>
      <div>
        <h1 className="font-lusitana text-4xl leading-tight mb-5">
          Seller Form
        </h1>

        {success ? (
          <>
            <div className="py-4">
              {finished ? (
                <div className="my-6 p-4 bg-navy rounded text-xl text-white">
                  <p>Thanks for your interest! We'd love to help you get rollin'! Buckle up, and we'll be in touch soon.</p>
                </div>
              ): (
                <>
                  <hr className="mb-6"/>
                  <h2 className="font-lusitana text-black text-4xl leading-tight mb-5">
                    Step 2: Upload Photos
                  </h2>
                  <p className="my-4">We recommend you uploading photos of the interior, exterior & kitchen.</p>
                  <PhotoUpload
                    token={data.token}
                    data={{
                      position: data.photos ? data.photos.length : 0,
                      subject: {
                        id: data.id,
                        _type: 'market_listings',
                      }
                    }}

                    onChange={(result) => afterUpload(result)}
                  />
                  <div className="grid grid-cols-4 gap-4 my-6">
                    {data.photos?.map(photo => {
                      return (
                        <div key={photo.id} style={{ paddingTop: '100%' }} className="relative cursor-pointer" >
                          <div
                            className={[
                              "absolute z-10 top-0 left-0 w-full h-full flex justify-center items-center",
                              "opacity-0 hover:opacity-100",
                            ]}
                            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                            onClick={() => removePhoto(photo)}
                          >
                            <Trash2 color="#FFF" size={30} />
                          </div>
                          <img
                            src={photo.url}
                            title={photo.title}
                            className="absolute top-0 left-0 w-full h-full object-cover"
                          />
                        </div>
                      )
                    })}
                  </div>

                  <hr className="my-6"/>

                    <button
                      onClick={setFinished}
                      data-test-id="finish"
                      className="btn btn-white font-lusitana text-2xl px-10 py-2"
                    >
                      Finish
                    </button>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="font-light text-1xl leading-tight mb-10">
              Please enter all of your truck details below. Adding a new listing is
              free. We charge a 10% listing fee on each completed transaction.
            </p>

            <FormWrapper className="w-full" onSubmit={submitForm}>
              <h3 className="text-2xl font-light leading-tight">
                Vehicle Information
              </h3>
              <hr className="mb-10" />

              <div className="my-4 md:flex">
                <label htmlFor="title" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Title</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    value={data.title}
                    data-test-id="title"
                    error={errors?.title || validations.title}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={title => {
                      setData(prev => ({
                        ...prev,
                        title,
                      }))
                    }}
                  />
                </div>
              </div>
              <div className="my-4 md:flex">
                <label htmlFor="price" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Price</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    value={data.price}
                    data-test-id="price"
                    type="number"
                    error={errors?.price || validations.price}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={price => {
                      setData(prev => ({
                        ...prev,
                        price,
                      }))
                    }}
                  />
                </div>
              </div>
              <div className="my-4">
                <label htmlFor="vehicle_city" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">City</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    value={data.vehicle_city}
                    data-test-id="vehicle_city"
                    error={errors?.vehicle_city || validations.vehicle_city}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={vehicle_city => {
                      setData(prev => ({
                        ...prev,
                        vehicle_city,
                      }))
                    }}
                  />
                </div>
              </div>
              <div className="my-4 md:flex">
                <label htmlFor="vehicle_state" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6 align-top">State (Country if not USA/CAN)</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Select
                    value={data.vehicle_state || undefined}
                    data-test-id="vehicle_state"
                    error={errors?.vehicle_state || validations.vehicle_state}
                    options={
                      [
                        { label: 'Choose a State', value: '' },
                      ].concat(
                        locations?.map((location, index) => (
                          { label: location.name, value: location.shortCode }
                        ))
                      )
                    }
                    classNames="w-full border rounded px-2 py-1"
                    onChange={value => {
                      setData({ ...data, 'vehicle_state': value })
                    }}
                  />
                </div>
              </div>
              <div className="my-4">
                <label htmlFor="vehicle_zip" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Zip</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    value={data.vehicle_zip}
                    data-test-id="vehicle_zip"
                    error={errors?.vehicle_zip || validations.vehicle_zip}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={vehicle_zip => {
                      setData(prev => ({
                        ...prev,
                        vehicle_zip,
                      }))
                    }}
                  />
                </div>
              </div>
              <div className="my-4 md:flex">
                <label htmlFor="year" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Year</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    type="number"
                    data-test-id="year"
                    value={data.year}
                    error={errors?.year || validations.year}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={year => {
                      setData(prev => ({
                        ...prev,
                        year,
                      }))
                    }}
                  />
                </div>
              </div>
              <div className="my-4 md:flex">
                <label htmlFor="make" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Make</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    value={data.make}
                    data-test-id="make"
                    error={errors?.make || validations.make}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={make => {
                      setData(prev => ({
                        ...prev,
                        make,
                      }))
                    }}
                  />
                </div>
              </div>
              <div className="my-4 md:flex">
                <label htmlFor="model" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Model</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    value={data.model}
                    data-test-id="model"
                    error={errors?.model || validations.model}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={model => {
                      setData(prev => ({
                        ...prev,
                        model,
                      }))
                    }}
                  />
                </div>
              </div>

              <div className="my-4">
                <label className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Type</label>
                <div className="w-full md:w-3/4 inline-block">
                  <input type="radio" name="vehicle_type_truck" value="truck" checked={data.vehicle_type === "truck"} onChange={e => setData({ ...data, 'vehicle_type': 'truck' })}/>
                  <label htmlFor="vehicle_type_truck" className="px-2">Truck</label>

                  <input type="radio" name="vehicle_type_trailer" value="trailer" checked={data.vehicle_type === "trailer"} onChange={e => setData({ ...data, 'vehicle_type': 'trailer' })} />
                  <label htmlFor="vehicle_type_trailer" className="px-2">Trailer</label>

                  <input type="radio" name="vehicle_type_cart" value="cart" checked={data.vehicle_type === "cart"} onChange={e => setData({ ...data, 'vehicle_type': 'cart' })} />
                  <label htmlFor="vehicle_type_cart" className="px-2">Cart</label>
                </div>
              </div>

              <div className="my-4 md:flex">
                <label htmlFor="mileage" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Mileage</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    type="number"
                    data-test-id="mileage"
                    value={data.mileage}
                    error={errors?.mileage || validations.mileage}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={mileage => {
                      setData(prev => ({
                        ...prev,
                        mileage,
                      }))
                    }}
                  />
                </div>
              </div>

              <div className="my-4">
                <label className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Fuel Type</label>
                <div className="w-full md:w-3/4 inline-block">
                  <input type="radio" name="fuel_type_gas" value="gas" checked={data.fuel_type === "gas"} onChange={e => setData({ ...data, 'fuel_type': 'gas' })} />
                  <label htmlFor="fuel_type_gas" className="px-2">Gas</label>

                  <input type="radio" name="fuel_type_diesel" value="diesel" checked={data.fuel_type === "diesel"} onChange={e => setData({ ...data, 'fuel_type': 'diesel' })} />
                  <label htmlFor="fuel_type_diesel" className="px-2">Diesel</label>
                </div>
              </div>

              <div className="my-4 md:flex">
                <label htmlFor="length" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Length</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    value={data.length}
                    data-test-id="length"
                    error={errors?.length}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={length => {
                      setData(prev => ({
                        ...prev,
                        length,
                      }))
                    }}
                  />
                </div>
              </div>

              <div className="my-4 md:flex">
                <label htmlFor="height" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Height</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    value={data.height}
                    data-test-id="height"
                    error={errors?.height}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={height => {
                      setData(prev => ({
                        ...prev,
                        height,
                      }))
                    }}
                  />
                </div>
              </div>

              <div className="my-4">
                <label htmlFor="permits" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Permits</label>
                <div className="w-full md:w-3/4 inline-block">
                  <Input
                    value={data.permits}
                    data-test-id="permits"
                    error={errors?.permits}
                    classNames={{ input: 'w-full border rounded px-2' }}
                    onChange={permits => {
                      setData(prev => ({
                        ...prev,
                        permits,
                      }))
                    }}
                  />
                </div>
              </div>

              <div className="my-4">
                <span className="w-full md:w-1/4 inline-block align-top md:text-right md:pl-2 pr-6">
                  Equipment
                </span>
                <div className="w-full md:w-3/4 inline-block overflow-auto">
                    {
                      featureCategories.data?.map(category => (
                        <div key={category.id} className="w-full md:w-1/2 float-left mb-6">
                          <h4 className="block uppercase tracking-wide text-gray-700 text-2xs mb-2">
                            {category.name}
                          </h4>
                          <hr />
                          {category.vehicle_features.map(feature => (
                            <label key={feature.id} className="block mt-3 font-bold">
                              <input
                                className="mr-2 leading-tight"
                                data-test-id="vehicle_features"
                                type="checkbox"
                                onChange={e => updateVehicleFeatures(feature)}
                              />
                              <span className="text-sm">{feature.name}</span>
                            </label>
                          ))}
                        </div>
                      ))
                    }
                </div>

                <div className="my-4">
                  <label htmlFor="additional_equipment" className="w-full md:w-1/4 inline-block align-top md:text-right md:pl-2 pr-6">Additional Equipment</label>
                  <div className="w-full md:w-3/4 inline-block">
                    <textarea name="additional_equipment" className="w-full border rounded px-2" cols="30" rows="6" onChange={e => setData({ ...data, 'additional_equipment': e.target.value })} />
                  </div>
                </div>

                <h3 className="text-2xl font-light leading-tight">
                  Contact Information
                </h3>
                <hr className="mb-10" />

                <div className="my-4 md:flex">
                  <label htmlFor="first_name" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">First Name</label>
                  <div className="w-full md:w-3/4 inline-block">
                    <Input
                      value={data.first_name}
                      data-test-id="first_name"
                      error={errors?.first_name || validations.first_name}
                      classNames={{ input: 'w-full border rounded px-2' }}
                      onChange={first_name => {
                        setData(prev => ({
                          ...prev,
                          first_name,
                        }))
                      }}
                    />
                  </div>
                </div>

                <div className="my-4 md:flex">
                  <label htmlFor="last_name" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Last Name</label>
                  <div className="w-full md:w-3/4 inline-block">
                    <Input
                      value={data.last_name}
                      data-test-id="last_name"
                      error={errors?.last_name || validations.last_name}
                      classNames={{ input: 'w-full border rounded px-2' }}
                      onChange={last_name => {
                        setData(prev => ({
                          ...prev,
                          last_name,
                        }))
                      }}
                    />
                  </div>
                </div>

                <div className="my-4 md:flex">
                  <label htmlFor="phone" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Phone</label>
                  <div className="w-full md:w-3/4 inline-block">
                    <Input
                      value={data.phone}
                      data-test-id="phone"
                      error={errors?.phone || validations.phone}
                      classNames={{ input: 'w-full border rounded px-2' }}
                      onChange={phone => {
                        if (phone && phone.length > (data.phone || '').length) {
                          phone = formatPhone(phone)
                        }
                        setData(prev => ({
                          ...prev,
                          phone,
                        }))
                      }}
                    />
                  </div>
                </div>

                <div className="my-4 md:flex">
                  <label htmlFor="email" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Email</label>
                  <div className="w-full md:w-3/4 inline-block">
                    <Input
                      value={data.email}
                      data-test-id="email"
                      error={errors?.email || validations.email}
                      classNames={{ input: 'w-full border rounded px-2' }}
                      onChange={email => {
                        setData(prev => ({
                          ...prev,
                          email,
                        }))
                      }}
                    />
                  </div>
                </div>

                <div className="my-4 md:flex">
                  <label htmlFor="address" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Address</label>
                  <div className="w-full md:w-3/4 inline-block">
                    <Input
                      value={data.address}
                      data-test-id="address"
                      error={errors?.address || validations.address}
                      classNames={{ input: 'w-full border rounded px-2' }}
                      onChange={address => {
                        setData(prev => ({
                          ...prev,
                          address,
                        }))
                      }}
                    />
                  </div>
                </div>
                <div className="my-4 md:flex">
                  <label htmlFor="city" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">City</label>
                  <div className="w-full md:w-3/4 inline-block">
                    <Input
                      value={data.city}
                      data-test-id="city"
                      error={errors?.city || validations.city}
                      classNames={{ input: 'w-full border rounded px-2' }}
                      onChange={city => {
                        setData(prev => ({
                          ...prev,
                          city,
                        }))
                      }}
                    />
                  </div>
                </div>
                <div className="my-4">
                  <label htmlFor="state" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6 align-top">State (Country if not USA/CAN)</label>
                  <div className="w-full md:w-3/4 inline-block">
                    <Select
                      value={data.state || undefined}
                      data-test-id="state"
                      error={errors?.state || validations.state}
                      options={
                        [
                          { label: 'Choose a State', value: '' },
                        ].concat(
                          locations?.map((location, index) => (
                            { label: location.name, value: location.shortCode }
                          ))
                        )
                      }
                      classNames="w-full border rounded px-2 py-1"
                      onChange={value => {
                        setData({ ...data, 'state': value })
                      }}
                    />
                  </div>
                </div>
                <div className="my-4 md:flex">
                  <label htmlFor="zip" className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">Zip</label>
                  <div className="w-full md:w-3/4 inline-block">
                    <Input
                      value={data.zip}
                      data-test-id="zip"
                      error={errors?.zip || validations.zip}
                      classNames={{ input: 'w-full border rounded px-2' }}
                      onChange={zip => {
                        setData(prev => ({
                          ...prev,
                          zip,
                        }))
                      }}
                    />
                  </div>
                </div>


                <div className="my-4">
                  <label htmlFor="terms_conditions" className="w-full md:w-1/4 inline-block align-top md:text-right md:pl-2 pr-6">
                    Terms &amp; Conditions
                  </label>
                  <TermsConditions />
                </div>

                <div className="my-4 text-right">
                  <label className="w-full md:w-3/4 inline-block text-left pr-6">
                    <input
                      className="mr-2 leading-tight"
                      data-test-id="terms"
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={e => setTermsAccepted(!termsAccepted)}
                    />
                    <span className="text-sm">
                      I agree to the above terms and conditions and to pay the 10%
                      listing fee on any completed transaction.
                    </span>
                  </label>
                </div>
              </div>
              <div className="my-4 text-right">
                <div className="md:w-3/4 inline-block text-left">
                  {errors || error && (
                    <div className="my-6 p-4 bg-red rounded text-xl text-white">
                      <p>There was a problem processing your information. Please check and try again.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    data-test-id="submit"
                    disabled={!termsAccepted}
                    className={["btn font-lusitana text-2xl px-10 py-2", termsAccepted ? 'btn-white' : 'btn-gray']}
                  >
                    Next: Upload Photos
                  </button>
                </div>
              </div>

            </FormWrapper>
          </>
        )}
      </div>
    </Layout>
  )
}


function TermsConditions() {
  return (
    <div className={[styles.sellformTermsScroll, 'w-full md:w-3/4 inline-block']}>
      <h5 className="text-2xl font-light font-lusitana leading-tight mb-5">
        LISTING AGREEMENT TO PROCURE CUSTOMERS
      </h5>
      <p>
        This Listing Agreement to Procure Customers (“Agreement”) is by and
        between Roaming Hunger LLC, a California corporation, whose address for
        purposes of this Agreement is 8360 Melrose Ave, SECOND FLOOR, Los Angeles, CA 90069 (“Company”) and the user, (“Seller”) and is
        effective immediately.
      </p>
      <h5 className="text-2xl font-light font-lusitana leading-tight my-5">
        RECITALS
      </h5>
      <ol type="A" className="list-decimal pl-5">
        <li>
          Company hosts a website which displays listings for the sales of food
          trucks and other products (each a “Product”). Customers seeking the
          purchase of Products access the Company's website (each a “Customer”).
        </li>
        <li>Seller desires to sell their Products to Customers.</li>
        <li>
          Seller wishes to engage Company to list its Products on the Company's
          website and agrees to compensate Company in accordance with this
          Agreement.
        </li>
      </ol>
      <h5 className="text-2xl font-light font-lusitana leading-tight my-5">
        AGREEMENT
      </h5>
      <p>
        NOW, THEREFORE, in exchange of the mutual covenants and promises
        contained herein, Seller and Company agree as follows:
      </p>
      <ol className="list-decimal pl-5">
        <li>
          <strong>Services Provided by Company.</strong> Upon request by Seller,
          Company shall list Seller's Product(s) for sale on Company's website.
          Seller authorizes company to: forward Customer leads to Seller,
          negotiate on behalf of Seller with Customers, underwrite or perform
          other due diligence tasks with any Customer, accept payment on behalf
          of Seller and retain any Fee (as defined below), recommend or arrange
          financing for any Customer, and any other task necessary to perform
          Company's responsibilities herein in Company's sole discretion for the
          benefit of Seller. Company does not guarantee it can procure any
          Customers for Seller, but shall use its best efforts to do so.
          <ol className="list-decimal pl-10">
            <li>Seller authorizes Roaming Hunger LLC to put a credit card hold in
              the amount of 10% of the total price of Products listed on the Company’s
              website. The hold is required before obtaining a Customers’
              contact info to arrange viewing of the Product.
            </li>
            <li>Once a sale to Customer occurs, I will provide documentation of
              the Gross Sales to Roaming Hunger LLC within 5 business days of
              sale. I agree to pay in full for the Listing Fee with this credit
              card and in accordance with the issuing bank cardholder agreement.
            </li>
          </ol>
        </li>
        <li>
          <strong>Company's Fees to Perform Services.</strong> During the term
          of this Agreement, the parties agree that Company shall receive a fee
          in consideration of the closing of any sale of any of Seller's
          Product(s) to a Customer. The fee shall be Ten percent (10%) of any
          gross sales amount for the sale of any Product of Seller (“Fee”)
          payable to Company. In Company's discretion, Company may collect the
          gross sales amount from any Customer, retain the Fee and disburse the
          net proceeds to Seller.
        </li>
        <li>
          <strong>Seller Will Pay Third Party Fees.</strong> Seller agrees to
          pay all of the following costs paid to third parties in advance if
          required by Company, or reimburse Company for costs paid on their
          behalf to third parties (“Third Party Fees”), including but not
          limited to any sales tax, credit reporting fees, governmental fees of
          any kind. Third Party Fees shall not include any fees to host the
          Seller's Products on Company's website.
        </li>
        <li>
          <strong>Termination of Agreement.</strong> This Agreement shall begin
          on the date set forth above. It shall have a three (3) year term.
          Notwithstanding the foregoing, Company shall be entitled to receive
          compensation pursuant to Section 2.1(b) of this Agreement after this
          Agreement terminates.
        </li>
        <li>
          <strong>
            Confidentiality, Non-Disclosure and Non-Circumvention.
          </strong>
          <ol className="list-decimal pl-10">
            <li>
              <strong>Confidentiality.</strong> In performing services herein,
              Company will disclose to Seller certain confidential information,
              trade secrets and/or other proprietary information of Company,
              including but not limited to the identity, contact information,
              credit information and other information required to assist Seller
              with the sale of its Products to any Customers (“Confidential
              Information”). Seller, including its affiliated companies, agents,
              employees, representatives, successors in interest or assigns,
              agrees to maintain the confidentiality and secrecy of any
              Confidential Information disclosed by Company. Seller acknowledges
              and agrees that the Confidential Information is valuable, special
              and unique to Company's business; that it is not widely known; and
              that Company's business depends on such Confidential Information.
              Seller acknowledges that Company has taken, and continues to take,
              reasonable and necessary steps to protect its Confidential
              Information and keep it confidential.
            </li>
            <li>
              <strong>Restrictions on Use.</strong> Seller covenants and agrees
              that:
              <ol type="a" className="list-disc pl-16">
                <li>
                  Confidential Information received by Seller from Company shall
                  be used only for purposes of discussing and evaluating a
                  potential sale of Seller's Products to Company's Customers and
                  for no other reason.
                </li>
                <li>
                  Confidential Information may not be used by Seller to the
                  detriment of Company.
                </li>
                <li>
                  Seller shall take all reasonable steps to safeguard and
                  protect Confidential Information disclosed by Company from any
                  theft, loss, unauthorized access, unauthorized use or
                  disclosure and accord it at least the same degree of
                  confidential and proprietary treatment as it gives its own
                  confidential and proprietary information. Unless otherwise
                  required by law, Seller will disclose Confidential Information
                  furnished to it by Company only to those of its agents,
                  employees, representatives on a need-to-know basis and will
                  notify such representatives who are provided any of Company's
                  Confidential Information, or who are involved in such
                  discussions or evaluation, or who may otherwise have occasion
                  to view, handle, or obtain any Confidential Information of
                  Company, of the terms of this Agreement and their obligation
                  to comply with each of them.
                </li>
                <li>
                  Seller shall not, without the prior written consent of
                  Company, which consent Company may withhold in its sole
                  discretion, use any Confidential Information.
                </li>
                <li>
                  Upon termination of this Agreement, Seller represents,
                  warrants and covenants that it will at its sole cost and
                  expense, return to Company, and shall not retain any
                  Confidential Information of Company or its employees, in
                  Seller's possession under the direction and control of Seller.
                </li>
              </ol>
            </li>
            <li>
              <strong>Non-Circumvention.</strong> Seller covenants and agrees
              that:
              <ol type="a" className="list-disc pl-16">
                <li>
                  Seller will not in any manner solicit nor accept any business
                  in any manner from Customers which were disclosed through this
                  Agreement, without the express permission of Company.
                </li>
                <li>
                  Seller will not in any way whatsoever circumvent or attempt to
                  circumvent Company.
                </li>
                <li>
                  Seller agrees that it, and all its affiliated companies,
                  agents, employees, successors in interest or assigns, will
                  not, during this Agreement's Term and for a three (3) year
                  period after this Agreement's termination, engage in Conduct
                  to Circumvent Company with third parties, including, without
                  limitation, Customers, suppliers, financial sources,
                  manufacturers, consultants, that have been disclosed to them,
                  directly or indirectly, by Company. "Conduct to Circumvent"
                  means contacting, discussing, or transacting business with
                  such third party, without the prior knowledge and written
                  consent of Company.
                </li>
                <li>
                  Seller will not disclose names, addresses, telephone numbers,
                  or fax numbers of any contact revealed by Company to other
                  third parties, and Seller agrees such contacts are the
                  exclusive property of the Company and that it will not enter
                  into direct negotiations or transactions with such contacts
                  revealed by Company, except as allowed in this Agreement.
                </li>
                <li>
                  In the event of any circumvention or attempted circumvention,
                  Company shall be entitled to a legal monetary penalty equal to
                  three times (3x) the maximum Fee it would have realized from
                  the sale of the Product, plus any and all expenses, including
                  legal, incurred to recover the lost revenue.
                </li>
              </ol>
            </li>
            <li>
              <strong>Liquidated Damages.</strong> In the event of a breach of
              Section 5 of this Agreement, except for a breach pursuant to
              section 5.3 in instances where the damages are readily
              ascertainable, each Party acknowledges that Company's Confidential
              Information is valuable, special and unique to Company's business;
              that it is not widely known; and that Company's business depends
              on such Confidential Information. If Seller breaches Section 2 of
              this Agreement, including a breach pursuant to section 5.3 in
              instances where the damages are not readily ascertainable, the
              Parties acknowledge that actual damages would be difficult to
              ascertain. Therefore, the Parties acknowledge that Seller must pay
              the sum of Fifteen Thousand Dollars ($15,000.00) to Company for
              each individual violation of this Agreement as liquidated damages.
              The Parties further acknowledge that the liquidated damages
              outlined herein represent a fair attempt to determine the damages
              that would be suffered and are not a penalty.
            </li>
            <li>
              <strong>Injunctive Relief.</strong> Notwithstanding section 5.4,
              in the event of the breach of this Agreement by any party, the
              non-breaching party shall be entitled to seek injunctive relief,
              both preliminary and permanent, enjoining and restraining such
              breach or threatened breach. Such remedy shall be in addition to
              all of the remedies available to the non-breaching party at law or
              in equity, including the right to recover from the breaching party
              any and all damages that may be sustained as a result of said
              breach. In addition to any other remedies available to the
              non-breaching party under this Agreement, the non-breaching party
              shall also be entitled to recover, by means of an accounting, any
              profits that the breaching party may have obtained as a result of
              such breach or threatened breach.
            </li>
            <li>
              <strong>Liability.</strong> Each Party shall be solely responsible
              for any breach of this Agreement by its affiliated companies,
              agents, employees, representatives, successors in interest or
              assigns, including without limitation, any improper use or
              disclosure by its affiliated companies, agents, employees,
              representatives, successors in interest or assigns of Company's
              Confidential Information.
            </li>
          </ol>
        </li>
        <li>
          <strong>Cooperation.</strong> Seller understands and agrees that they
          will fully cooperate with Company to sell Seller's Products to
          Customers. Such cooperation shall include, but is not limited to,
          signing all necessary documents, supplemental instructions,
          instruments and disclosures, permitting access to the Seller's
          property for an appraisal and inspection and promptly supplying
          information to the Company that it reasonably requests.
        </li>
        <li>
          <strong>Notices.</strong> Any notice required or permitted by this
          Agreement shall be in writing and shall be deemed sufficient upon
          receipt, when delivered personally or by courier, overnight delivery
          service or confirmed facsimile, or three (3) business days after being
          deposited in the regular mail as certified or registered mail (airmail
          if sent internationally) with postage prepaid, if such notice is
          addressed to the party to be notified at such party's address or
          facsimile number (as applicable) set forth above.
        </li>
        <li>
          <strong>Jurisdiction/Venue.</strong> The laws of the State of
          California shall govern this Agreement. If a dispute arises in
          connection with or relating to this Agreement, it shall be subject to
          the exclusive jurisdiction and venue of the state and federal courts
          located in Los Angeles, California, and the parties consent to the
          personal and exclusive jurisdiction and venue of these courts.
        </li>
        <li>
          <strong>Miscellaneous.</strong> This Agreement may not be amended or
          modified orally but may be amended or modified only in writing, signed
          by all parties hereto. No waiver of any term or provision of this
          Agreement shall be effective unless it is in writing, making specific
          reference to this Agreement and signed by the party against whom such
          waiver is sought to be enforced. This Agreement constitutes the entire
          agreement between the parties hereto with respect to the subject
          matter hereof. This Agreement shall be binding upon the parties hereto
          and their respective heirs, legal representatives, successors and
          assigns. This Agreement shall be governed by and construed in
          accordance with the laws of the State of California.
        </li>
        <li>
          <strong>Counterparts.</strong> This Agreement may be executed in two
          (2) or more counterparts, each of which shall be deemed an original
          and all of which together shall constitute and be construed as one and
          the same instrument.
        </li>
        <li>
          <strong>Attorneys' Fees.</strong> In the event any dispute or breach
          under this Agreement, the prevailing party shall be entitled, whether
          or not any action or arbitration is instituted, to recover from the
          other party its reasonable costs, disbursements and attorneys' fees,
          including without limitation at trial, on appeal, on denial of any
          petition for review, and in connection with the enforcement of any
          judgment.
        </li>
        <li>
          <strong>Severability.</strong> Every provision of this Agreement shall
          be construed, to the extent possible, so as to be valid and
          enforceable. If any provision of this Agreement so construed is held
          by a court of competent jurisdiction to be invalid, illegal or
          otherwise unenforceable, such provision shall be deemed severed from
          this Agreement, and all other provisions shall remain in full force
          and effect.
        </li>
        <li>
          <strong>Joint Drafting.</strong> This Agreement shall be construed as
          if it were drafted by all parties hereto. Any uncertainty or ambiguity
          existing in the Agreement, if any, shall not be interpreted against
          any party, bu according to the application of the rules of
          interpretation of contracts. Section and article headings used herein
          are for convenience only and shall not be a part of the substance or
          interpretation of the Agreement.
        </li>
      </ol>
      <p>
        IN WITNESS WHEREOF, the parties have caused this Agreement to be
        executed by its duly authorized officers or signatory and of the date
        first hereinabove written.
      </p>
      <p>
        Acceptance of this agreement via the website is binding and in lieu of a
        signature.
      </p>
    </div>
  )
}
